<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import type { EnableCameraScene } from "../../Phaser/Login/EnableCameraScene";
    import { EnableCameraSceneName } from "../../Phaser/Login/EnableCameraScene";
    import {
        requestedCameraDeviceIdStore,
        batchGetUserMediaStore,
        cameraListStore,
        localVolumeStore,
        requestedMicrophoneDeviceIdStore,
        microphoneListStore,
        speakerListStore,
        requestedCameraState,
        requestedMicrophoneState,
        speakerSelectedStore,
        localStreamStore,
    } from "../../Stores/MediaStore";
    import type { Game } from "../../Phaser/Game/Game";
    import { LL, locale } from "../../../i18n/i18n-svelte";
    import { myCameraStore, myMicrophoneStore } from "../../Stores/MyMediaStore";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { playSpeakerTestSound } from "../../WebRtc/AudioOutputManager";

    import bgMap from "../images/map-exemple.webp";
    import Button from "../UI/Button.svelte";
    import HorizontalSoundMeterWidget from "./HorizontalSoundMeterWidget.svelte";
    import SelectMicrophone from "./SelectMicrophone.svelte";
    import SelectCamera from "./SelectCamera.svelte";
    import SelectSpeaker from "./SelectSpeaker.svelte";
    import { IconMicrophoneOn, IconCamera } from "@wa-icons";

    interface Props {
        game: Game;
    }

    let { game }: Props = $props();

    let enableCameraScene = $derived(game.scene.getScene(EnableCameraSceneName) as EnableCameraScene);
    const bgColor = gameManager.currentStartedRoom.backgroundColor ?? "#1B2A41";
    const sceneBg = gameManager.currentStartedRoom.backgroundSceneImage ?? bgMap;
    let legals = gameManager.currentStartedRoom?.legals ?? {};

    let selectedCamera: string | undefined = $state(undefined);
    let selectedMicrophone: string | undefined = $state(undefined);

    let legalStrings: string[] = [];
    if (legals?.termsOfUseUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.termsOfUseUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.termsOfUse() +
                "</a>",
        );
    }
    if (legals?.privacyPolicyUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.privacyPolicyUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.privacyPolicy() +
                "</a>",
        );
    }
    if (legals?.cookiePolicyUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.cookiePolicyUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.cookiePolicy() +
                "</a>",
        );
    }

    let legalString: string | undefined = $state();
    if (legalStrings.length > 0) {
        if (Intl.ListFormat) {
            const formatter = new Intl.ListFormat($locale, { style: "long", type: "conjunction" });
            legalString = formatter.format(legalStrings);
        } else {
            // For old browsers
            legalString = legalStrings.join(", ");
        }
    }

    function submit() {
        // Picking a device or "Disable" is applied at click time (see handleSelect*). Re-applying the displayed
        // selection here would turn off a device whose track is not delivered yet, and pin the browser's default device
        // as the preferred one. A saved preferred device is what marks this page as done (see GameManager): "" means
        // no preference, follow the system default.
        localUserStore.setPreferredVideoInputDevice(localUserStore.getPreferredVideoInputDevice() ?? "");
        localUserStore.setPreferredAudioInputDevice(localUserStore.getPreferredAudioInputDevice() ?? "");
        enableCameraScene.login();
    }

    function srcObject(node: HTMLVideoElement, stream: MediaStream) {
        node.srcObject = stream;
        return {
            update(newStream: MediaStream) {
                if (node.srcObject != newStream) {
                    node.srcObject = newStream;
                }
            },
        };
    }

    let stream: MediaStream | undefined;

    const unsubscribeLocalStreamStore = localStreamStore.subscribe((value) => {
        if (value.type === "success") {
            stream = value.stream;

            if (stream !== undefined) {
                const videoTracks = stream.getVideoTracks();
                if (videoTracks.length > 0) {
                    selectedCamera = videoTracks[0].getSettings().deviceId;
                } else {
                    selectedCamera = undefined;
                }
                const audioTracks = stream.getAudioTracks();
                if (audioTracks.length > 0) {
                    selectedMicrophone = audioTracks[0].getSettings().deviceId;
                } else {
                    selectedMicrophone = undefined;
                }
            }
        } else {
            stream = undefined;
            selectedCamera = undefined;
            selectedMicrophone = undefined;
        }
    });

    onDestroy(() => {
        unsubscribeLocalStreamStore();
    });

    onMount(() => {
        //init the component to enable webcam and microphone
        batchGetUserMediaStore.startBatch();
        myCameraStore.set(true);
        myMicrophoneStore.set(true);
        requestedCameraState.enableWebcam();

        requestedMicrophoneState.enableMicrophone();

        batchGetUserMediaStore.commitChanges();
    });

    function handleSelectCamera(deviceId: string | undefined) {
        selectCamera(deviceId);
    }

    function handleSelectMicrophone(device: string | undefined) {
        selectMicrophone(device);
    }

    function handleSelectSpeaker(deviceId: string | undefined) {
        selectSpeaker(deviceId);
    }

    function selectCamera(newCameraSelected: string | undefined = undefined) {
        selectedCamera = newCameraSelected;
        if (!selectedCamera) {
            localUserStore.setPreferredVideoInputDevice("");
            requestedCameraState.disableWebcam();
            return;
        }
        requestedCameraState.enableWebcam();
        requestedCameraDeviceIdStore.set(selectedCamera);
        localUserStore.setPreferredVideoInputDevice(selectedCamera);
    }

    function selectMicrophone(newMicrophoneSelected: string | undefined = undefined) {
        selectedMicrophone = newMicrophoneSelected;
        if (!selectedMicrophone) {
            localUserStore.setPreferredAudioInputDevice("");
            requestedMicrophoneState.disableMicrophone();
            return;
        }
        requestedMicrophoneState.enableMicrophone();
        requestedMicrophoneDeviceIdStore.set(selectedMicrophone);
        localUserStore.setPreferredAudioInputDevice(selectedMicrophone);
    }

    function selectSpeaker(deviceId: string | undefined) {
        localUserStore.setSpeakerDeviceId(deviceId ?? "");
        speakerSelectedStore.set(deviceId);
    }

    function playSoundClick() {
        playSpeakerTestSound().catch((e) => console.error(e));
    }
    /* eslint-disable svelte/no-at-html-tags */
</script>

<form
    class="enableCameraScene pointer-events-auto relative z-30 m-0 px-2"
    onsubmit={(event) => {
        event.preventDefault();
        submit();
    }}
>
    <section class="flex min-h-dvh">
        <div
            class="text-white justify-center items-center overflow-hidden w-[100vw] container flex flex-col min-h-dvh pb-42 md:pb-36 pt-8 lg:pt-0 relative"
        >
            <section class="mb-4 text-center">
                <h2 class="h4">{$LL.camera.enable.title()}</h2>
                <p class="opacity-50 w-2/3 m-auto pt-2 hidden md:block">
                    {$LL.camera.enable.start()}
                </p>
            </section>

            <div
                class="flex md:flex-wrap flex-col gap-4 w-full items-center justify-center lg:flex-row lg:items-stretch lg:px-4 lg:pb-4"
            >
                <!-- MICROPHONE -->

                <SelectMicrophone
                    onselectdevice={handleSelectMicrophone}
                    deviceList={$microphoneListStore ?? []}
                    selectedDevice={selectedMicrophone}
                >
                    {#snippet icon()}
                        <IconMicrophoneOn font-size="24" />
                    {/snippet}
                    {#snippet title()}
                        <span>{$LL.actionbar.subtitle.microphone()}</span>
                    {/snippet}
                    {#snippet widget()}
                        <div class="absolute top-4 start-0 flex justify-center w-full">
                            <HorizontalSoundMeterWidget spectrum={$localVolumeStore} />
                        </div>
                    {/snippet}
                </SelectMicrophone>

                <!-- CAMERA -->
                <SelectCamera
                    onselectdevice={handleSelectCamera}
                    deviceList={$cameraListStore ?? []}
                    selectedDevice={selectedCamera}
                >
                    {#snippet icon()}
                        <IconCamera font-size="24" />
                    {/snippet}
                    {#snippet title()}
                        <span>{$LL.camera.editCam()}</span>
                    {/snippet}
                    {#snippet widget()}
                        <span>
                            {#if selectedCamera !== undefined && $localStreamStore.type === "success" && $localStreamStore.stream}
                                <video
                                    class="myCamVideoSetup bg-contrast/80 backdrop-blur flex items-center justify-center w-full aspect-video overflow-hidden scale-x-[-1]"
                                    use:srcObject={$localStreamStore.stream}
                                    autoplay
                                    muted
                                    playsinline
                                ></video>
                            {:else}
                                <div
                                    class="webrtcsetup flex items-center justify-center w-full aspect-video rounded-lg overflow-hidden bg-contrast"
                                >
                                    {$LL.camera.my.loading()}
                                </div>
                            {/if}
                        </span>
                    {/snippet}
                </SelectCamera>

                <!-- SPEAKER -->
                {#if $speakerSelectedStore != undefined && $speakerListStore && $speakerListStore.length > 0}
                    <SelectSpeaker
                        onplaysound={playSoundClick}
                        onselectdevice={handleSelectSpeaker}
                        deviceList={$speakerListStore ?? []}
                        selectedDevice={$speakerSelectedStore}
                    />
                {/if}
            </div>
            <div
                class="fixed bottom-0 start-0 !w-[100vw] bg-contrast/80 backdrop-blur-md border border-solid border-t border-b-0 border-x-0 border-white/10"
            >
                <section
                    class="container m-auto p-4 flex flex-col-reverse md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4"
                >
                    <Button type="submit" variant="light" appearance="ghost" size="lg" class="w-full md:w-1/2 hidden">
                        {$LL.actionbar.cancel()}
                    </Button>
                    <!-- TODO ACTION -->
                    <Button type="submit" variant="secondary" size="lg" class="w-full md:w-1/2 block">
                        {$LL.menu.settings.save()}
                    </Button>
                </section>
                {#if legalString}
                    <section class="terms-and-conditions h-fit z-40 text-center w-full">
                        <a style="display: none;" href="traduction">Need for traduction</a>
                        <p class="text-white text-xs italic opacity-50">
                            {@html $LL.login.terms({
                                links: legalString,
                            })}
                        </p>
                    </section>
                {/if}
            </div>
        </div>
    </section>
</form>
<div class="fixed start-0 top-0 w-dvw h-dvh bg-cover z-10" style="background-image: url('{sceneBg}');"></div>
<div class="fixed start-0 top-0 w-dvw h-dvh bg-contrast/80 z-20" style="background-color: '{bgColor}';"></div>

<style>
    .enableCameraScene {
        h2 {
            margin: 1px;
        }

        section.text-center {
            text-align: center;
        }
    }
</style>
